import { createTheme } from "@mui/material/styles";

/**
 * Bridge MUI theme — WhatsPRO (green + black brand).
 *
 * The app renders MUI components; this theme mirrors the CSS variable tokens
 * (src/styles/globals.css) so every MUI surface matches the kit. Built as a
 * factory so light + dark share the same component overrides with different
 * token values.
 */

const lightTokens = {
  mode: "light",
  bg: "#F9F8FF",
  card: "#FFFFFF",
  surface2: "#F3F1FB",
  surface2Hover: "#ECEBF8",
  ink: "#262254",
  muted: "#6E6B85",
  line: "#E3E3E3",
  lineStrong: "#D3D0E4",
  brand: "#0DA15A",
  brandTint: "#E4F7EC",
  brandDark: "#0A8449",
  brandInk: "#FFFFFF",
  success: "#12996B",
  successTint: "#E1F5EE",
  successInk: "#0E7A55",
  warning: "#B8860B",
  warningTint: "#FBEFC9",
  warningInk: "#8A6200",
  error: "#DC2A2A",
  errorTint: "#FCE0E1",
  errorInk: "#C81A22",
  info: "#0DA15A",
  backdrop: "rgba(38,34,84,.42)",
};

const darkTokens = {
  mode: "dark",
  bg: "#0E1613",
  card: "#16211C",
  surface2: "#1E2A24",
  surface2Hover: "#24332B",
  ink: "#E8F1EC",
  muted: "#9FB2A8",
  line: "#29372F",
  lineStrong: "#3B4B42",
  brand: "#2ED588",
  brandTint: "#16281F",
  brandDark: "#25B473",
  brandInk: "#06231A",
  success: "#35C18F",
  successTint: "#123026",
  successInk: "#8FE9C6",
  warning: "#E9C46A",
  warningTint: "#332C15",
  warningInk: "#E9C46A",
  error: "#F87171",
  errorTint: "#391A1D",
  errorInk: "#F8A1A1",
  info: "#2ED588",
  backdrop: "rgba(0,0,0,.6)",
};

function buildShadows(t) {
  const sm =
    t.mode === "dark"
      ? "0 1px 2px rgba(0,0,0,.4),0 3px 8px -4px rgba(0,0,0,.6)"
      : "0 1px 2px rgba(38,34,84,.05),0 3px 8px -4px rgba(38,34,84,.09)";
  const lg =
    t.mode === "dark"
      ? "0 6px 16px -6px rgba(0,0,0,.55),0 22px 48px -18px rgba(0,0,0,.75)"
      : "0 4px 12px -4px rgba(38,34,84,.13),0 18px 40px -16px rgba(38,34,84,.22)";
  return { sm, lg, arr: ["none", sm, sm, sm, ...Array(21).fill(lg)] };
}

function buildTheme(t) {
  const { sm: shadowSm, lg: shadowLg, arr: shadows } = buildShadows(t);
  const brandShadow =
    t.mode === "dark"
      ? "0 1px 2px rgba(0,0,0,.4), 0 6px 16px -6px rgba(46,213,136,.35)"
      : "0 1px 2px rgba(13,161,90,.25), 0 6px 16px -6px rgba(13,161,90,.45)";
  return createTheme({
    palette: {
      mode: t.mode,
      primary: { main: t.brand, dark: t.brandDark, light: t.brandTint, contrastText: t.brandInk },
      secondary: { main: t.mode === "dark" ? "#0B1F17" : "#12241C", contrastText: "#fff" },
      success: { main: t.success, light: t.successTint, contrastText: t.brandInk },
      warning: { main: t.warning, light: t.warningTint, contrastText: t.mode === "dark" ? "#231C09" : "#fff" },
      error: { main: t.error, light: t.errorTint, contrastText: t.mode === "dark" ? "#1A0808" : "#fff" },
      info: { main: t.info, contrastText: t.brandInk },
      background: { default: t.bg, paper: t.card },
      text: { primary: t.ink, secondary: t.muted },
      divider: t.line,
    },
    shape: { borderRadius: 12 },
    shadows,
    typography: {
      fontFamily: '"Poppins", ui-sans-serif, system-ui, sans-serif',
      h1: { fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.05 },
      h2: { fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.1 },
      h3: { fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15 },
      h4: { fontWeight: 600, letterSpacing: "-0.02em" },
      h5: { fontWeight: 600, letterSpacing: "-0.015em" },
      h6: { fontWeight: 600, letterSpacing: "-0.01em" },
      subtitle1: { fontWeight: 500 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600, textTransform: "none", letterSpacing: 0 },
      overline: { fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontWeight: 600,
            textTransform: "none",
            paddingInline: 18,
            paddingBlock: 9,
            transition: "background-color .2s cubic-bezier(.23,1,.32,1), box-shadow .2s, transform .12s",
            "&:active": { transform: "translateY(1px)" },
          },
          sizeLarge: { paddingBlock: 12, paddingInline: 24, fontSize: "0.95rem" },
          sizeSmall: { paddingBlock: 6, paddingInline: 12 },
          containedPrimary: {
            boxShadow: brandShadow,
            "&:hover": { backgroundColor: t.brandDark },
          },
          outlined: { borderColor: t.line, "&:hover": { borderColor: t.brand, backgroundColor: t.brandTint } },
          text: { "&:hover": { backgroundColor: t.surface2 } },
        },
      },
      MuiIconButton: { styleOverrides: { root: { borderRadius: 10, "&:hover": { backgroundColor: t.surface2 } } } },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: "none", backgroundColor: t.card, color: t.ink },
          rounded: { borderRadius: 16 },
          outlined: { borderColor: t.line },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { borderRadius: 16, border: `1px solid ${t.line}`, boxShadow: shadowSm, backgroundImage: "none", backgroundColor: t.card },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: t.card,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: t.line },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.lineStrong },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: t.brand, borderWidth: 1.5 },
          },
          input: { paddingBlock: 13 },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: { borderRadius: 10, backgroundColor: t.surface2, "&:hover": { backgroundColor: t.surface2Hover }, "&.Mui-focused": { backgroundColor: t.surface2 } },
        },
      },
      MuiInputLabel: { styleOverrides: { root: { color: t.muted } } },
      MuiTextField: { defaultProps: { variant: "outlined" } },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 100, fontWeight: 600, fontSize: "0.78rem" },
          outlined: { borderColor: t.line },
          filledPrimary: { backgroundColor: t.brandTint, color: t.brand },
          filledSuccess: { backgroundColor: t.successTint, color: t.successInk },
          filledError: { backgroundColor: t.errorTint, color: t.errorInk },
          filledWarning: { backgroundColor: t.warningTint, color: t.warningInk },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: "inherit" },
        styleOverrides: { root: { backgroundColor: t.card, color: t.ink, borderBottom: `1px solid ${t.line}`, backgroundImage: "none" } },
      },
      MuiDrawer: { styleOverrides: { paper: { backgroundColor: t.card, borderColor: t.line, backgroundImage: "none" } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: 18, boxShadow: shadowLg, backgroundImage: "none", backgroundColor: t.card } } },
      MuiDialogTitle: { styleOverrides: { root: { fontWeight: 700, letterSpacing: "-0.01em" } } },
      MuiMenu: { styleOverrides: { paper: { borderRadius: 12, border: `1px solid ${t.line}`, boxShadow: shadowLg, backgroundImage: "none", backgroundColor: t.card } } },
      MuiMenuItem: { styleOverrides: { root: { borderRadius: 8, marginInline: 6, "&:hover": { backgroundColor: t.surface2 } } } },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: t.mode === "dark" ? "#000000" : t.ink, borderRadius: 8, fontSize: "0.75rem", fontWeight: 500, padding: "6px 10px" },
          arrow: { color: t.mode === "dark" ? "#000000" : t.ink },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: t.line, color: t.ink },
          head: { fontWeight: 700, color: t.muted, backgroundColor: t.surface2, textTransform: "none" },
        },
      },
      MuiTableContainer: { styleOverrides: { root: { borderRadius: 14, border: `1px solid ${t.line}` } } },
      MuiTabs: { styleOverrides: { root: { minHeight: 42 }, indicator: { height: 3, borderRadius: 3, backgroundColor: t.brand } } },
      MuiTab: { styleOverrides: { root: { textTransform: "none", fontWeight: 600, minHeight: 42, "&.Mui-selected": { color: t.brand } } } },
      MuiSwitch: {
        styleOverrides: {
          root: { padding: 8 },
          track: { borderRadius: 22, backgroundColor: t.lineStrong, opacity: 1 },
          thumb: { boxShadow: "0 1px 2px rgba(0,0,0,.3)" },
        },
      },
      MuiLinearProgress: { styleOverrides: { root: { borderRadius: 100, backgroundColor: t.surface2 }, bar: { borderRadius: 100 } } },
      MuiDivider: { styleOverrides: { root: { borderColor: t.line } } },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 12, fontWeight: 500 },
          standardSuccess: { backgroundColor: t.successTint, color: t.successInk },
          standardError: { backgroundColor: t.errorTint, color: t.errorInk },
          standardWarning: { backgroundColor: t.warningTint, color: t.warningInk },
          standardInfo: { backgroundColor: t.brandTint, color: t.brand },
        },
      },
      MuiLink: { defaultProps: { underline: "hover" }, styleOverrides: { root: { color: t.brand, fontWeight: 500 } } },
      MuiAvatar: { styleOverrides: { root: { fontWeight: 600 } } },
      MuiBackdrop: { styleOverrides: { root: { backgroundColor: t.backdrop } } },
    },
  });
}

export const muiTheme = buildTheme(lightTokens);
export const muiThemeDark = buildTheme(darkTokens);
export default muiTheme;
